import React from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';

const ONEDOC_URL = "https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid";

const ReequilibrageAlimentaire = () => {
  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title="Rééquilibrage alimentaire à Vevey, Clarens & Lausanne — Nutritionniste"
        description="Rééquilibrage alimentaire personnalisé à Vevey, Clarens, Montreux et région lausannoise. Sortir des régimes, retrouver une relation saine à l'alimentation et un équilibre durable."
        keywords="rééquilibrage alimentaire Vevey, rééquilibrage alimentaire Lausanne, nutritionniste Lausanne, perte de poids durable Vaud, sortir des régimes, alimentation équilibrée Riviera, accompagnement nutrition canton de Vaud"
        url="https://nutricentreriviera.ch/reequilibrage-alimentaire-vevey-lausanne"
      />

      {/* Hero */}
      <section style={{
        background: 'var(--gradient-hero)',
        padding: '5rem 0 4rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <span className="caption" style={{
            color: 'var(--accent-primary)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '0.85rem',
            display: 'inline-block',
            marginBottom: '1rem'
          }}>
            Rééquilibrage alimentaire
          </span>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Rééquilibrage alimentaire à Vevey, Clarens et région lausannoise
          </h1>
          <p className="heading-subtitle" style={{ maxWidth: '780px', margin: '0 auto 2rem', lineHeight: 1.6 }}>
            En finir avec les régimes, les frustrations et les reprises de poids. Retrouver un équilibre alimentaire qui vous correspond, durablement, sans dogme. Un accompagnement personnalisé sur la Riviera vaudoise — pour les habitantes de Vevey, Clarens, Montreux et Lausanne.
          </p>
          <a href={ONEDOC_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ textDecoration: 'none' }} data-testid="reequilibrage-hero-cta">
            Prendre rendez-vous
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Vous vous reconnaissez si... */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            Vous vous reconnaissez si…
          </h2>
          <p className="body-large" style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '2.5rem', lineHeight: 1.7 }}>
            Le rééquilibrage alimentaire est l'alternative aux régimes — pour celles qui veulent des résultats durables, sans frustration.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {[
              "Vous avez enchaîné les régimes — et à chaque fois, repris le poids perdu (voire plus).",
              "Vous vivez avec une charge mentale alimentaire permanente : compter, peser, calculer, culpabiliser.",
              "Vous voulez retrouver le plaisir de manger sans peur.",
              "Vous savez ce qu'il faut manger « en théorie », mais vous n'arrivez pas à le mettre en place durablement.",
              "Vous avez l'impression de ne plus avoir de repères clairs sur ce qui vous fait vraiment du bien.",
              "Vous voulez perdre du poids — mais sans souffrir, sans interdire, sans yo-yo.",
              "Vous cherchez un accompagnement basé sur votre vie réelle, pas sur un menu standardisé.",
              "Vous souhaitez transmettre une relation saine à l'alimentation à vos enfants."
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex',
                gap: '0.75rem',
                padding: '1.1rem 1.25rem',
                background: 'var(--bg-page)',
                borderRadius: '0.85rem',
                border: '1px solid var(--border-light)',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: 'var(--accent-primary)', flexShrink: 0, marginTop: '0.5rem'
                }} />
                <p className="body-medium" style={{ margin: 0, lineHeight: 1.55 }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi les régimes échouent */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
            Pourquoi un vrai rééquilibrage n'est pas un régime
          </h2>
          <p className="body-large" style={{ marginBottom: '1.5rem', lineHeight: 1.7 }}>
            La majorité des régimes échouent dans la durée — non par manque de volonté, mais par <strong>logique même</strong>. Un rééquilibrage alimentaire fonctionne différemment :
          </p>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {[
              ["Il respecte votre métabolisme.", "Pas de privation extrême qui ralentit votre corps et déclenche le yo-yo."],
              ["Il s'adapte à votre vie.", "Repas en famille, déplacements, vie sociale, horaires de travail — votre quotidien guide la stratégie, pas l'inverse."],
              ["Il intègre tous les aliments.", "Aucun interdit alimentaire. Aucune frustration. Le plaisir reste un pilier."],
              ["Il agit sur la cause, pas le symptôme.", "Stabilité glycémique, équilibre hormonal, sommeil, stress, comportement alimentaire — tout est lié."],
              ["Il s'inscrit dans la durée.", "Pas un sprint, mais un cheminement progressif et solide vers un nouvel équilibre."],
              ["Il prend en compte les émotions.", "Manger est aussi émotionnel. Un vrai rééquilibrage l'intègre — pas le contraire."]
            ].map(([t, d], i) => (
              <li key={i} style={{
                background: 'white',
                border: '1px solid var(--border-light)',
                borderLeft: '4px solid var(--accent-primary)',
                borderRadius: '0.75rem',
                padding: '1.1rem 1.4rem'
              }}>
                <p className="body-medium" style={{ marginBottom: '0.35rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t}</p>
                <p className="body-medium" style={{ margin: 0, color: 'var(--text-muted)', lineHeight: 1.55 }}>{d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Notre approche */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Notre méthode de rééquilibrage à Vevey, Clarens & Lausanne
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Nous accueillons les personnes accompagnées de toute la Riviera vaudoise et de la région lausannoise — au cabinet de Vevey ou de Clarens (Montreux). Chaque accompagnement est entièrement personnalisé.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {[
              ["1. Le bilan", "Anamnèse approfondie, analyse corporelle, bio-impédancemétrie. Nous identifions ce qui dérègle votre équilibre — bien au-delà du simple chiffre sur la balance."],
              ["2. La stratégie", "Construction d'un plan alimentaire sur mesure, qui s'intègre dans votre vie. Suivi via l'application Nutrium pour fluidité au quotidien."],
              ["3. L'accompagnement", "Consultations régulières pour ajuster, soutenir et installer durablement le nouvel équilibre — sans rechute, sans yo-yo."]
            ].map(([t, d], i) => (
              <div key={i} style={{
                background: 'var(--bg-page)',
                borderRadius: '1rem',
                padding: '1.75rem',
                border: '1px solid var(--border-light)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: 'var(--accent-primary)' }}>{t}</h3>
                <p className="body-medium" style={{ color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--accent-primary)', color: 'white', padding: '5rem 0', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: '720px' }}>
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            Prête pour un vrai rééquilibrage, durable et sans frustration ?
          </h2>
          <p className="body-large" style={{ color: 'white', opacity: 0.95, marginBottom: '2rem', lineHeight: 1.7 }}>
            Réservez votre consultation au cabinet de Vevey ou de Clarens. Accessible depuis Montreux, Lausanne et tout le canton de Vaud. Remboursement possible par certaines assurances complémentaires (ASCA &amp; RME).
          </p>
          <a href={ONEDOC_URL} target="_blank" rel="noopener noreferrer" className="btn-primary"
             style={{ background: 'white', color: 'var(--accent-primary)', textDecoration: 'none' }}
             data-testid="reequilibrage-final-cta">
            Réserver ma consultation
            <ArrowRight size={20} />
          </a>
          <p className="caption" style={{
            marginTop: '1rem', color: 'white', opacity: 0.9,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
          }}>
            <CheckCircle2 size={16} />
            Vevey · Clarens · Montreux · Lausanne · canton de Vaud
          </p>
        </div>
      </section>

      <RelatedPages items={relatedPagesMap['reequilibrage-alimentaire-vevey-lausanne']} />
    </div>
  );
};

export default ReequilibrageAlimentaire;
